import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Translate } from 'react-redux-i18n';
import MiniLoader from 'components/MiniLoader';
import * as optionActions from 'redux/actions/optionOrderActions';
import * as stockActions from 'redux/actions/stockOrderActions';
import * as quoteActions from 'redux/actions/quoteActions';
import useStockQuotes from 'hooks/useStockQuotes';
import useStockCandles from 'hooks/useStockCandles';
import useOptionQuotes, { buildOccTicker } from 'hooks/useOptionQuotes';
import { collectLegs, formatContractLabel } from 'utils/optionPayoff';
import {
    ANALYSIS_KIND_OPTION,
    ANALYSIS_KIND_STOCK,
} from 'utils/portfolioNav';
import OptionPayoffChart from './OptionPayoffChart';
import TradingViewChart from 'components/TradingViewChart';
import 'assets/css/analysis.css';

function resolveStockPrice(stockMap, symbol, fallbackAsk) {
    const quote = stockMap?.[symbol];
    const fromQuote = Number(quote?.c || quote?.ask || quote?.p);
    if (Number.isFinite(fromQuote) && fromQuote > 0) {
        return fromQuote;
    }
    const ask = Number(fallbackAsk);
    return Number.isFinite(ask) && ask > 0 ? ask : 0;
}

function findPositionById(positions, optionId) {
    if (optionId == null) {
        return null;
    }
    const target = String(optionId);

    for (const item of positions) {
        if (String(item.id) === target) {
            return item;
        }
        const legs = collectLegs(item);
        const match = legs.find((leg) => String(leg.id) === target);
        if (match) {
            return Number(item.counter) > 1 || (Array.isArray(item.sub_order) && item.sub_order.length > 1)
                ? item
                : match;
        }
    }
    return null;
}

function findStockById(positions, stockId, symbol) {
    if (stockId != null) {
        const target = String(stockId);
        const byId = positions.find((item) => String(item.id) === target);
        if (byId) {
            return byId;
        }
    }
    if (symbol) {
        const sym = String(symbol).toUpperCase();
        return positions.find((item) => String(item.symbol || '').toUpperCase() === sym) || null;
    }
    return null;
}

function formatDate(value) {
    if (!value) {
        return '—';
    }
    try {
        return String(value).slice(0, 10);
    } catch (e) {
        return '—';
    }
}

function resolveOptionPrice(optionMap, leg) {
    const ticker = buildOccTicker(leg);
    const live = ticker ? Number(optionMap?.[ticker]?.c) : 0;
    if (Number.isFinite(live) && live > 0) {
        return live;
    }
    const fallback = Number(leg?.option_price);
    return Number.isFinite(fallback) && fallback > 0 ? fallback : 0;
}

function calculateOptionMetrics(legs, optionMap) {
    let netLiq = 0;
    let openCapital = 0;
    const prices = [];

    legs.forEach((leg) => {
        const price = resolveOptionPrice(optionMap, leg);
        const quantity = Math.abs(Number(leg.quantity) || 0);
        const multiplier = 100;
        const market = String(leg.market || '').toUpperCase();
        const direction = market === 'SELL' ? -1 : 1;
        const openTrade = Number(leg.open_trade);
        const hasOpenTrade = leg.open_trade != null
            && leg.open_trade !== ''
            && Number.isFinite(openTrade);
        const fallbackOpenTrade = (market === 'SELL' ? 1 : -1)
            * Math.abs(Number(leg.open_price) || 0)
            * quantity
            * multiplier;

        prices.push(price);
        netLiq += direction * price * quantity * multiplier;
        openCapital += Math.abs(hasOpenTrade ? openTrade : fallbackOpenTrade);
    });

    const openTradeTotal = legs.reduce((sum, leg) => {
        const quantity = Math.abs(Number(leg.quantity) || 0);
        const market = String(leg.market || '').toUpperCase();
        const stored = Number(leg.open_trade);
        if (leg.open_trade != null && leg.open_trade !== '' && Number.isFinite(stored)) {
            return sum + stored;
        }
        const signed = market === 'SELL' ? 1 : -1;
        return sum + signed * Math.abs(Number(leg.open_price) || 0) * quantity * 100;
    }, 0);
    const pl = openTradeTotal + netLiq;
    const plPercent = openCapital > 0 ? (pl / openCapital) * 100 : 0;

    return {
        prices,
        netLiq,
        pl,
        plPercent,
    };
}

const Analysis = (props) => {
    const kind = props.analysisFocus?.kind || ANALYSIS_KIND_OPTION;
    const [stockTimeframe, setStockTimeframe] = useState('1d');

    useEffect(() => {
        if (!props.optionList?.length) {
            props.loadOptionList(props.user_id, props.token);
        }
        if (!props.stockList?.length) {
            props.loadStockList(props.user_id, props.token);
        }
    }, [props.user_id]);

    const quoteSource = kind === ANALYSIS_KIND_STOCK ? props.stockList : props.optionList;
    useStockQuotes(quoteSource, props.user_id, props.token, props.loadStockQuotes);
    useOptionQuotes(
        props.optionList,
        props.user_id,
        props.token,
        props.loadOptionQuotes,
    );

    const optionPositions = useMemo(
        () => (Array.isArray(props.optionList) ? props.optionList : []),
        [props.optionList],
    );
    const stockPositions = useMemo(
        () => (Array.isArray(props.stockList) ? props.stockList : []),
        [props.stockList],
    );

    const selectedOption = useMemo(() => {
        if (kind !== ANALYSIS_KIND_OPTION) {
            return null;
        }
        const focused = findPositionById(optionPositions, props.analysisFocus?.id);
        return focused || optionPositions[0] || null;
    }, [kind, optionPositions, props.analysisFocus]);

    const selectedStock = useMemo(() => {
        if (kind !== ANALYSIS_KIND_STOCK) {
            return null;
        }
        const focused = findStockById(
            stockPositions,
            props.analysisFocus?.id,
            props.analysisFocus?.symbol,
        );
        return focused || stockPositions[0] || null;
    }, [kind, stockPositions, props.analysisFocus]);

    const selectedStockSymbol = selectedStock
        ? String(selectedStock.symbol || '').toUpperCase()
        : '';
    const {
        candles: stockCandles,
        loading: stockCandlesLoading,
        error: stockCandlesError,
    } = useStockCandles(
        selectedStockSymbol,
        stockTimeframe,
        props.user_id,
        kind === ANALYSIS_KIND_STOCK && Boolean(selectedStockSymbol),
    );

    const optionStockPrice = selectedOption
        ? resolveStockPrice(props.quotes, selectedOption.symbol, selectedOption.ask)
        : 0;

    const stockLivePrice = selectedStock
        ? resolveStockPrice(props.quotes, selectedStock.symbol, selectedStock.ask)
        : 0;

    const optionLegs = selectedOption ? collectLegs(selectedOption) : [];
    const optionMetrics = calculateOptionMetrics(optionLegs, props.optionQuotes);
    const loading = props.optionLoading || props.stockLoading;

    if (kind === ANALYSIS_KIND_STOCK) {
        return (
            <div className="analysis-page">
                {loading && stockPositions.length === 0 ? (
                    <div className="analysis-empty">
                        <MiniLoader />
                    </div>
                ) : !selectedStock ? (
                    <p className="analysis-empty">
                        <Translate value="analysis_stock_empty" />
                    </p>
                ) : (
                    <div className="analysis-main analysis-main--full">
                        <div className="analysis-contract-detail">
                            <div className="analysis-contract-detail-main">
                                <h2 className="analysis-contract-detail-title">
                                    {String(selectedStock.symbol || '').toUpperCase()}
                                </h2>
                                <p className="analysis-contract-detail-sub">
                                    <Translate value="analysis_stock_subtitle" />
                                </p>
                            </div>
                            <div className="analysis-contract-detail-grid">
                                <div className="analysis-detail-chip">
                                    <span className="analysis-detail-chip-label">
                                        <Translate value="analysis_detail_symbol" />
                                    </span>
                                    <span className="analysis-detail-chip-value">
                                        {String(selectedStock.symbol || '').toUpperCase()}
                                    </span>
                                </div>
                                <div className="analysis-detail-chip">
                                    <span className="analysis-detail-chip-label">
                                        <Translate value="analysis_detail_side" />
                                    </span>
                                    <span className="analysis-detail-chip-value">
                                        {selectedStock.market || '—'}
                                    </span>
                                </div>
                                <div className="analysis-detail-chip">
                                    <span className="analysis-detail-chip-label">
                                        <Translate value="analysis_detail_qty" />
                                    </span>
                                    <span className="analysis-detail-chip-value">
                                        {selectedStock.quantity != null ? selectedStock.quantity : '—'}
                                    </span>
                                </div>
                                <div className="analysis-detail-chip">
                                    <span className="analysis-detail-chip-label">
                                        <Translate value="analysis_stock_entry" />
                                    </span>
                                    <span className="analysis-detail-chip-value">
                                        ${Number(selectedStock.open_price || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div className="analysis-detail-chip">
                                    <span className="analysis-detail-chip-label">
                                        <Translate value="analysis_detail_open" />
                                    </span>
                                    <span className="analysis-detail-chip-value">
                                        {formatDate(selectedStock.open_at)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <TradingViewChart
                            symbol={selectedStockSymbol || 'STOCK'}
                            basePrice={stockLivePrice || Number(selectedStock.open_price) || 0}
                            entryPrice={Number(selectedStock.open_price) || 0}
                            candles={stockCandles}
                            loading={stockCandlesLoading}
                            error={stockCandlesError}
                            timeframe={stockTimeframe}
                            onTimeframeChange={setStockTimeframe}
                            height={520}
                            drawingsEnabled
                            userId={props.user_id}
                        />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="analysis-page">
            {loading && optionPositions.length === 0 ? (
                <div className="analysis-empty">
                    <MiniLoader />
                </div>
            ) : !selectedOption ? (
                <p className="analysis-empty">
                    <Translate value="analysis_empty" />
                </p>
            ) : (
                <div className="analysis-main analysis-main--full">
                    <div className="analysis-contract-detail">
                        <div className="analysis-contract-detail-main">
                            <h2 className="analysis-contract-detail-title">
                                {formatContractLabel(selectedOption)}
                            </h2>
                            <p className="analysis-contract-detail-sub">
                                <Translate value="analysis_subtitle" />
                            </p>
                        </div>
                        <div className="analysis-contract-detail-grid">
                            <div className="analysis-detail-chip">
                                <span className="analysis-detail-chip-label">
                                    <Translate value="analysis_detail_symbol" />
                                </span>
                                <span className="analysis-detail-chip-value">
                                    {String(selectedOption.symbol || '').toUpperCase()}
                                </span>
                            </div>
                            <div className="analysis-detail-chip">
                                <span className="analysis-detail-chip-label">
                                    <Translate value="analysis_detail_side" />
                                </span>
                                <span className="analysis-detail-chip-value">
                                    {selectedOption.market || (optionLegs[0] && optionLegs[0].market) || '—'}
                                </span>
                            </div>
                            <div className="analysis-detail-chip">
                                <span className="analysis-detail-chip-label">
                                    <Translate value="analysis_detail_qty" />
                                </span>
                                <span className="analysis-detail-chip-value">
                                    {selectedOption.quantity != null
                                        ? selectedOption.quantity
                                        : (optionLegs[0] && optionLegs[0].quantity) || '—'}
                                </span>
                            </div>
                            <div className="analysis-detail-chip">
                                <span className="analysis-detail-chip-label">
                                    <Translate value="analysis_detail_strike" />
                                </span>
                                <span className="analysis-detail-chip-value">
                                    {optionLegs.length === 1
                                        ? (optionLegs[0].strike != null ? optionLegs[0].strike : '—')
                                        : optionLegs.map((l) => l.strike).filter((s) => s != null).join(' / ') || '—'}
                                </span>
                            </div>
                            <div className="analysis-detail-chip">
                                <span className="analysis-detail-chip-label">
                                    <Translate value="analysis_detail_type" />
                                </span>
                                <span className="analysis-detail-chip-value">
                                    {optionLegs.length === 1
                                        ? (optionLegs[0].option_kind || '—')
                                        : optionLegs.map((l) => l.option_kind).filter(Boolean).join(' / ') || '—'}
                                </span>
                            </div>
                            <div className="analysis-detail-chip">
                                <span className="analysis-detail-chip-label">
                                    <Translate value="analysis_detail_premium" />
                                </span>
                                <span className="analysis-detail-chip-value">
                                    {optionLegs.length === 1
                                        ? `$${Number(optionLegs[0].open_price || 0).toFixed(2)}`
                                        : `$${Math.abs(Number(selectedOption.open_trade || 0)).toFixed(2)}`}
                                </span>
                            </div>
                            <div className="analysis-detail-chip">
                                <span className="analysis-detail-chip-label">
                                    <Translate value="analysis_detail_expire" />
                                </span>
                                <span className="analysis-detail-chip-value">
                                    {formatDate(selectedOption.expire_at || (optionLegs[0] && optionLegs[0].expire_at))}
                                </span>
                            </div>
                            <div className="analysis-detail-chip">
                                <span className="analysis-detail-chip-label">
                                    <Translate value="analysis_detail_open" />
                                </span>
                                <span className="analysis-detail-chip-value">
                                    {formatDate(selectedOption.open_at || (optionLegs[0] && optionLegs[0].open_at))}
                                </span>
                            </div>
                        </div>
                    </div>

                    <OptionPayoffChart
                        position={selectedOption}
                        stockPrice={optionStockPrice}
                        metrics={optionMetrics}
                    />
                </div>
            )}
        </div>
    );
};

Analysis.propTypes = {
    analysisFocus: PropTypes.shape({
        kind: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        symbol: PropTypes.string,
    }),
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
        token: reducer.session.auth.token,
        optionList: reducer.optionOrder.list,
        optionLoading: reducer.optionOrder.loading,
        stockList: reducer.stockOrder.list,
        stockLoading: reducer.stockOrder.loading,
        quotes: reducer.event.stock,
        optionQuotes: reducer.event.option,
    };
}

const mapDispatchToProps = (dispatch) => ({
    loadOptionList: (user_id, token) =>
        dispatch(optionActions.loadList({ user_id, token })),
    loadStockList: (user_id, token) =>
        dispatch(stockActions.loadList({ user_id, token })),
    loadStockQuotes: (symbols, user_id, token) =>
        dispatch(quoteActions.loadStockQuotes({ symbols, user_id, token })),
    loadOptionQuotes: (tickers, user_id, token) =>
        dispatch(quoteActions.loadOptionQuotes({ tickers, user_id, token })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Analysis);
